import React, { Suspense, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import MarkdownRenderer from './MarkdownRenderer';
import demoRegistry from './demoRegistry';
import { useContent } from './contentContext';
import './learn.scss';

const ConceptDetail = () => {
  const { topicId, conceptId } = useParams<{ topicId: string; conceptId: string }>();
  const { topics, concepts, loading, error } = useContent();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const concept = concepts.find((c) => c.topic === topicId && c.id === conceptId);

  useEffect(() => {
    if (concept) document.title = `${concept.title} — Ruman KB`;
    return () => {
      document.title = 'Ruman Website';
    };
  }, [concept]);

  useEffect(() => {
    if (!concept) return;
    setMarkdown(null);
    setFetchError(null);
    fetch(`${process.env.PUBLIC_URL || ''}${concept.path}`)
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.text();
      })
      .then(setMarkdown)
      .catch((err) => setFetchError(String(err)));
  }, [concept]);

  if (loading) return <Typography>Loading…</Typography>;
  if (error) return <Typography color="error">Failed to load content: {error}</Typography>;
  if (!concept) {
    return (
      <div>
        <Typography variant="h5">Concept not found.</Typography>
        <Link to="/learn">Back to topics</Link>
      </div>
    );
  }

  const topic = topics.find((t) => t.id === concept.topic);
  const Demo = concept.demo ? demoRegistry[concept.demo] : undefined;

  return (
    <div className="concept-detail">
      <Link className="concept-back" to={`/learn/${concept.topic}`}>
        ← {topic ? topic.title : concept.topic}
      </Link>
      <Typography variant="h3" component="h1" gutterBottom>
        {concept.title}
      </Typography>
      <div className="concept-tags">
        {concept.tags.map((tag) => (
          <Chip key={tag} label={tag} size="small" />
        ))}
      </div>

      {Demo && (
        <div className="concept-demo">
          <div className="concept-demo-label">Live example</div>
          <Suspense fallback={<Typography>Loading demo…</Typography>}>
            <Demo />
          </Suspense>
        </div>
      )}

      <div className="concept-doc">
        {fetchError && (
          <Typography color="error">Failed to load article: {fetchError}</Typography>
        )}
        {markdown != null && <MarkdownRenderer markdown={markdown} />}
      </div>
    </div>
  );
};

export default ConceptDetail;
